import {
  Show,
  SimpleShowLayout,
  TextField,
  DateField,
  useRecordContext,
  TopToolbar,
  EditButton,
  DeleteButton,
  usePermissions,
} from 'react-admin';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Grid,
  Paper,
} from '@mui/material';

const VerificationStatusChip = () => {
  const record = useRecordContext();
  if (!record) return null;

  const status = record.verification_status || 'unverified';
  const colorMap: Record<string, 'default' | 'warning' | 'success'> = {
    unverified: 'default',
    pending_review: 'warning',
    verified: 'success',
  };

  return (
    <Chip
      label={status.replace('_', ' ')}
      color={colorMap[status] || 'default'}
      size="small"
    />
  );
};

const SearchTokensField = () => {
  const record = useRecordContext();
  if (!record?.search_tokens) return null;

  return (
    <Box display="flex" flexWrap="wrap" gap={0.5}>
      {record.search_tokens.map((token: string, index: number) => (
        <Chip key={index} label={token} size="small" variant="outlined" />
      ))}
    </Box>
  );
};

const AttributesDisplay = () => {
  const record = useRecordContext();
  if (!record?.attributes) return null;

  return (
    <Paper
      sx={{
        p: 2,
        backgroundColor: '#f5f5f5',
        fontFamily: 'monospace',
        fontSize: '0.875rem',
        overflow: 'auto',
        maxHeight: 500,
      }}
    >
      <pre style={{ margin: 0 }}>
        {JSON.stringify(record.attributes, null, 2)}
      </pre>
    </Paper>
  );
};

const ShowActions = () => {
  const { permissions } = usePermissions();
  const canEdit =
    permissions?.role === 'super_admin' ||
    permissions?.permissions?.includes('entities:update');
  const canDelete =
    permissions?.role === 'super_admin' ||
    permissions?.permissions?.includes('entities:delete');

  return (
    <TopToolbar>
      {canEdit && <EditButton />}
      {canDelete && <DeleteButton />}
    </TopToolbar>
  );
};

export const EntityShow = () => {
  return (
    <Show actions={<ShowActions />}>
      <SimpleShowLayout>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    Display Name
                  </Typography>
                  <TextField source="display_name" />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    Entity Type
                  </Typography>
                  <TextField source="entity_type" />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    Search Tokens
                  </Typography>
                  <SearchTokensField />
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Attributes
                </Typography>
                <AttributesDisplay />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Verification
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    Status
                  </Typography>
                  <VerificationStatusChip />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    Verified At
                  </Typography>
                  <DateField source="verified_at" showTime emptyText="Not verified" />
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Metadata
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    ID
                  </Typography>
                  <TextField source="id" />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    Created
                  </Typography>
                  <DateField source="created_at" showTime />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    Updated
                  </Typography>
                  <DateField source="updated_at" showTime />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </SimpleShowLayout>
    </Show>
  );
};
